import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import type { ToolCallMessagePartProps } from "@assistant-ui/react";

type StepStatus = "running" | "completed" | "failed";

function toolLabel(name: string): string {
  const text = name.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function isErrorResult(result: unknown): boolean {
  if (typeof result === "string") {
    return result.startsWith("Error invoking tool") || result.startsWith("Error:");
  }
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    return r.exito === false || r.error !== undefined;
  }
  return false;
}

function deriveStatus(
  status: ToolCallMessagePartProps["status"],
  result: unknown,
): StepStatus {
  if (status.type === "running") return "running";
  if (result !== undefined && isErrorResult(result)) return "failed";
  if (status.type === "complete" || result !== undefined) return "completed";
  return "failed";
}

/* ── Admin view: full expandable tool card ── */

function ToolCallStepAdmin(props: ToolCallMessagePartProps) {
  const { toolName, args, argsText, result, status: rawStatus } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const status = deriveStatus(rawStatus, result);
  const hasArgs = (args && Object.keys(args).length > 0) || (argsText && argsText !== "{}" && argsText !== "");
  const hasResult = result !== undefined;

  const statusIcon: Record<StepStatus, React.ReactNode> = {
    running: <Loader2 size={13} className="shrink-0 animate-spin text-[var(--primary)]" />,
    completed: <Check size={13} className="shrink-0 text-green-500" />,
    failed: <AlertCircle size={13} className="shrink-0 text-[var(--primary)]" />,
  };

  const hasDetails = hasArgs || hasResult;

  return (
    <div className="my-1.5 rounded-lg border border-[var(--border)] bg-[var(--muted)] text-sm">
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`flex w-full items-center gap-2 px-3 py-2 ${hasDetails ? "cursor-pointer hover:bg-[var(--border)]/30" : "cursor-default"}`}
      >
        {hasDetails && (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        {statusIcon[status]}
        <span className={status === "running" ? "font-medium" : "text-[var(--muted-foreground)]"}>
          {toolLabel(toolName)}
        </span>
        <span className="text-xs text-[var(--muted-foreground)]">
          {t(`tools.${status}`)}
        </span>
      </button>

      {expanded && hasDetails && (
        <div className="border-t border-[var(--border)] px-3 py-2">
          {hasArgs && (
            <>
              <div className="mb-1 text-xs text-[var(--muted-foreground)]">Input:</div>
              <pre className="mb-2 overflow-x-auto rounded bg-[var(--background)] p-2 text-xs">
                {args && Object.keys(args).length > 0
                  ? JSON.stringify(args, null, 2)
                  : argsText}
              </pre>
            </>
          )}
          {hasResult && (
            <>
              <div className="mb-1 text-xs text-[var(--muted-foreground)]">Output:</div>
              <pre className="overflow-x-auto rounded bg-[var(--background)] p-2 text-xs">
                {typeof result === "string"
                  ? (() => { try { return JSON.stringify(JSON.parse(result), null, 2); } catch { return result; } })()
                  : JSON.stringify(result, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── User view: status indicator with tool name (running + completed) ── */

function ToolCallStepUser(props: ToolCallMessagePartProps) {
  const { toolName, status: rawStatus, result } = props;
  const { t } = useTranslation();
  const status = deriveStatus(rawStatus, result);

  const icon: Record<StepStatus, React.ReactNode> = {
    running: <Loader2 size={13} className="shrink-0 animate-spin" />,
    completed: <Check size={13} className="shrink-0 text-green-500" />,
    failed: <AlertCircle size={13} className="shrink-0 text-[var(--primary)]" />,
  };

  return (
    <div className="flex items-center gap-2 py-1.5 text-sm text-[var(--muted-foreground)]">
      {icon[status]}
      <span>
        {toolLabel(toolName)}
        {status === "running" ? "…" : ""}
      </span>
      {status !== "running" && (
        <span className="text-xs opacity-70">{t(`tools.${status}`)}</span>
      )}
    </div>
  );
}

/* ── Exported wrapper — delegates based on isAdmin prop ── */

export default function ToolCallStep(props: ToolCallMessagePartProps & { isAdmin?: boolean }) {
  const { isAdmin, ...rest } = props;
  if (isAdmin) return <ToolCallStepAdmin {...rest} />;
  return <ToolCallStepUser {...rest} />;
}
